import { isRecord } from '@/lib/shared/guards/is-record'

/**
 * Typed Convex app-error payload. The only import is the zero-import
 * `isRecord` leaf, so Convex, the web app, and admin share one code list.
 *
 * `isRecord` rejects arrays. An array is never a structured app error.
 */
export const APP_ERROR_KIND = 'app_error'

export const APP_ERROR_CODES = [
  'NOT_FOUND',
  'NOT_AUTHENTICATED',
  'NOT_AUTHORIZED',
  'VALIDATION_ERROR',
  'CONFLICT',
  'INVALID_STATE',
  'LIMIT_REACHED',
  'EXTERNAL_SERVICE_ERROR',
  'EMPTY_SHELL',
  'CONSERVATION_FAILED',
  // The caller's org is suspended by the platform kill switch (`orgBlocks`).
  // Distinct from NOT_AUTHORIZED so API/MCP surfaces can present "account
  // suspended" instead of "invalid credentials".
  'ORG_BLOCKED',
] as const

export type AppErrorCode = (typeof APP_ERROR_CODES)[number]

export type AppErrorData = {
  kind: typeof APP_ERROR_KIND
  code: AppErrorCode
  message: string
  field?: string
  retryable?: boolean
}

export function isAppErrorData(value: unknown): value is AppErrorData {
  if (!isRecord(value)) {
    return false
  }

  return (
    value.kind === APP_ERROR_KIND &&
    typeof value.code === 'string' &&
    APP_ERROR_CODES.includes(value.code as AppErrorCode) &&
    typeof value.message === 'string' &&
    (!('field' in value) || typeof value.field === 'string') &&
    (!('retryable' in value) || typeof value.retryable === 'boolean')
  )
}

function hasConvexErrorData(error: unknown): error is { data: unknown } {
  return isRecord(error) && 'data' in error
}

export function getConvexAppError(error: unknown): AppErrorData | null {
  if (!hasConvexErrorData(error)) {
    return null
  }

  return isAppErrorData(error.data) ? error.data : null
}
